import React, { useState, useEffect } from 'react'
import axios from 'axios'
// @ts-ignore
import Select from 'react-select'
import { Select as MuiSelect, MenuItem, FormControl } from '@mui/material'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { Moment } from 'moment-timezone'

interface Location {
  id: number
  name: string
  organization_name: string
}

interface Organization {
  id: number
  name: string
  locations: Location[]
}

interface CurrentFilters {
  level?: string
  business?: string
  organizationIds?: number[]
  locationIds?: number[]
  startDate?: string
  endDate?: string
}

interface SentryAlertsFiltersProps {
  levels: string[]
  currentFilters: CurrentFilters
  is_scoped: boolean
}

interface SelectOption {
  value: number
  label: string
}

const SentryAlertsFilters: React.FC<SentryAlertsFiltersProps> = ({
  levels,
  currentFilters,
  is_scoped
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [selectedOrganizations, setSelectedOrganizations] = useState<SelectOption[]>([])
  const [selectedLocations, setSelectedLocations] = useState<SelectOption[]>([])
  const [isBusiness, setIsBusiness] = useState<boolean>(currentFilters.business === 'true')
  const [level, setLevel] = useState<string>(currentFilters.level || '')
  const [startDate, setStartDate] = useState<Moment | null>(
    currentFilters.startDate ? moment(currentFilters.startDate) : null
  )
  const [endDate, setEndDate] = useState<Moment | null>(
    currentFilters.endDate ? moment(currentFilters.endDate) : null
  )

  useEffect(() => {
    if (!is_scoped) {
      axios.get('/api/internal/locations/organizations_with_locations')
        .then(response => {
          setOrganizations(response.data)
        })
        .catch(error => {
          console.error('Failed to fetch organizations:', error)
        })
    }
  }, [is_scoped])

  useEffect(() => {
    if (organizations.length === 0) return

    if (currentFilters.organizationIds) {
      const orgIds = Array.isArray(currentFilters.organizationIds)
        ? currentFilters.organizationIds
        : [currentFilters.organizationIds]
      const matchedOrgs = organizations.filter(org => orgIds.includes(org.id))
      setSelectedOrganizations(matchedOrgs.map(org => ({ value: org.id, label: org.name })))
    }

    if (currentFilters.locationIds) {
      const locIds = Array.isArray(currentFilters.locationIds)
        ? currentFilters.locationIds
        : [currentFilters.locationIds]
      const allLocations = organizations.flatMap(org => org.locations)
      const matchedLocs = allLocations.filter(loc => locIds.includes(loc.id))
      setSelectedLocations(matchedLocs.map(loc => ({ value: loc.id, label: `${loc.name} (${loc.organization_name})` })))
    }
  }, [organizations])

  const organizationOptions: SelectOption[] = organizations.map(org => ({
    value: org.id,
    label: org.name
  }))

  const availableLocations = selectedOrganizations.length > 0
    ? organizations
        .filter(org => selectedOrganizations.some(selected => selected.value === org.id))
        .flatMap(org => org.locations)
    : organizations.flatMap(org => org.locations)

  const locationOptions: SelectOption[] = availableLocations.map(loc => ({
    value: loc.id,
    label: `${loc.name} (${loc.organization_name})`
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const params = new URLSearchParams()

    if (level) params.append('level', level)
    if (isBusiness) params.append('business', 'true')
    if (startDate) params.append('start_date', startDate.format('YYYY-MM-DD'))
    if (endDate) params.append('end_date', endDate.format('YYYY-MM-DD'))

    if (!is_scoped) {
      selectedOrganizations.forEach(org => {
        params.append('organization_ids[]', org.value.toString())
      })

      selectedLocations.forEach(loc => {
        params.append('location_ids[]', loc.value.toString())
      })
    }

    window.location.href = `/admin/sentry_alerts?${params.toString()}`
  }

  const handleClear = () => {
    window.location.href = '/admin/sentry_alerts'
  }

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '38px',
      borderColor: '#dee2e6'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#757575',
      padding: '8px',
      ':hover': {
        color: '#424242'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#F1F1F2'
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#3A3A3A'
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#3A3A3A',
      ':hover': {
        backgroundColor: '#F1F1F2',
        color: 'white'
      }
    })
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Business Alerts</label>
                  <div className="form-check" style={{ paddingTop: '8px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="businessCheck"
                      checked={isBusiness}
                      onChange={(e) => setIsBusiness(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="businessCheck">
                      Show only business
                    </label>
                  </div>
                </div>

                <div className="col-md-8">
                  <label className="form-label">Level</label>
                  <FormControl fullWidth size="small">
                    <MuiSelect
                      value={level}
                      onChange={(e) => setLevel(e.target.value)}
                      displayEmpty
                      IconComponent={() => (
                        <i className="fa fa-chevron-down" style={{ color: '#757575', marginRight: '12px', fontWeight: 300, fontSize: '12px' }}/>
                      )}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#dee2e6'
                        },
                        '& .MuiSelect-icon': {
                          top: 'calc(50% - 5px)',
                          right: '8px'
                        }
                      }}
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      {levels.map(l => (
                        <MenuItem key={l} value={l}>
                          {l.charAt(0).toUpperCase() + l.slice(1)}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          {!is_scoped && (
            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <label className="form-label">Organization</label>
                <Select
                  isMulti
                  options={organizationOptions}
                  value={selectedOrganizations}
                  onChange={(selected) => {
                    setSelectedOrganizations(selected as SelectOption[])
                    setSelectedLocations([])
                  }}
                  placeholder="Select organizations..."
                  styles={selectStyles}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Location</label>
                <Select
                  isMulti
                  options={locationOptions}
                  value={selectedLocations}
                  onChange={(selected) => setSelectedLocations(selected as SelectOption[])}
                  placeholder="Select locations..."
                  styles={selectStyles}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isDisabled={selectedOrganizations.length === 0 && locationOptions.length === 0}
                />
                {selectedOrganizations.length > 0 && locationOptions.length === 0 && (
                  <div className="form-text text-muted">
                    No locations found for selected organizations
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SentryAlertsFilters
